"use client"

import { useEffect } from "react"

const STORAGE_KEY = "meridian.landing.scroll"
const MAX_SNAPSHOT_AGE_MS = 10 * 60 * 1000

interface ScrollSnapshot {
  y: number
  createdAt: number
}

function isScrollSnapshot(value: unknown): value is ScrollSnapshot {
  if (value === null || typeof value !== "object") {
    return false
  }

  const candidate = value as Record<string, unknown>
  return typeof candidate.y === "number" && typeof candidate.createdAt === "number"
}

function readSnapshot(): ScrollSnapshot | null {
  const rawValue = window.sessionStorage.getItem(STORAGE_KEY)
  if (rawValue === null) {
    return null
  }

  try {
    const parsedValue: unknown = JSON.parse(rawValue)
    if (!isScrollSnapshot(parsedValue)) {
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    const ageMs = Date.now() - parsedValue.createdAt
    if (ageMs > MAX_SNAPSHOT_AGE_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    return parsedValue
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

function restoreSnapshot(): void {
  if (window.location.hash.length > 1) {
    window.sessionStorage.removeItem(STORAGE_KEY)
    return
  }

  const snapshot = readSnapshot()
  if (snapshot === null) {
    return
  }

  window.sessionStorage.removeItem(STORAGE_KEY)

  const scrollOptions: ScrollToOptions = {
    top: snapshot.y,
    left: 0,
    behavior: "auto",
  }

  window.requestAnimationFrame(function restoreOnNextFrame(): void {
    window.scrollTo(scrollOptions)
    window.setTimeout(function restoreAfterLayout(): void {
      window.scrollTo(scrollOptions)
    }, 100)
  })
}

function saveSnapshot(): void {
  const snapshot: ScrollSnapshot = {
    y: window.scrollY,
    createdAt: Date.now(),
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
}

function isInternalPublicationLink(target: Element): boolean {
  const anchor = target.closest("a") as HTMLAnchorElement | null
  if (anchor === null) {
    return false
  }

  const href = anchor.getAttribute("href")
  if (href === null) {
    return false
  }

  if (href.startsWith("/publications/")) {
    return true
  }

  try {
    const linkUrl = new URL(anchor.href)
    return linkUrl.origin === window.location.origin && linkUrl.pathname.startsWith("/publications/")
  } catch {
    return false
  }
}

function shouldHandleClick(event: MouseEvent): boolean {
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

function getSamePageHashLink(target: Element): HTMLAnchorElement | null {
  const anchor = target.closest("a") as HTMLAnchorElement | null
  if (anchor === null) {
    return null
  }

  const href = anchor.getAttribute("href")
  if (href === null) {
    return null
  }

  try {
    const linkUrl = new URL(anchor.href)
    if (linkUrl.origin !== window.location.origin) {
      return null
    }

    if (linkUrl.pathname !== window.location.pathname) {
      return null
    }

    if (linkUrl.hash.length <= 1) {
      return null
    }

    return anchor
  } catch {
    return null
  }
}

function getHashTargetElement(anchor: HTMLAnchorElement): HTMLElement | null {
  const linkUrl = new URL(anchor.href)
  const id = decodeURIComponent(linkUrl.hash.substring(1))
  if (id.length === 0) {
    return null
  }

  return document.getElementById(id)
}

function getSmoothScrollBehavior(): ScrollBehavior {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return "auto"
  }

  return "smooth"
}

function scrollToHashTarget(event: MouseEvent, target: Element): boolean {
  if (!shouldHandleClick(event)) {
    return false
  }

  const anchor = getSamePageHashLink(target)
  if (anchor === null) {
    return false
  }

  const targetElement = getHashTargetElement(anchor)
  if (targetElement === null) {
    return false
  }

  event.preventDefault()

  const linkUrl = new URL(anchor.href)
  window.history.pushState(null, "", linkUrl.hash)

  const scrollOptions: ScrollIntoViewOptions = {
    behavior: getSmoothScrollBehavior(),
    block: "start",
  }

  targetElement.scrollIntoView(scrollOptions)
  return true
}

export default function LandingScrollMemory() {
  useEffect(function handleLandingScrollMemory(): () => void {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"
    }

    restoreSnapshot()

    function handleDocumentClick(event: MouseEvent): void {
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }

      if (scrollToHashTarget(event, target)) {
        return
      }

      if (isInternalPublicationLink(target)) {
        saveSnapshot()
      }
    }

    document.addEventListener("click", handleDocumentClick, true)

    return function cleanupLandingScrollMemory(): void {
      document.removeEventListener("click", handleDocumentClick, true)
    }
  }, [])

  return null
}
